import { useId, useRef } from 'react';
import { CheckIcon, DownloadIcon, RefreshCwIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LibraryItemView } from '@/lib/api/generated/model';
import { actionFor } from '../actions';
import { stateBadges } from '../labels';
import { missingParts, missingPartsLabel } from '../missingParts';
import { rowSummary } from '../rowSummary';
import { InlineCode } from './InlineCode';
import { StateBadge } from './StateBadge';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * One item, as a row: a catalog of tens of items with a line of description
 * each is a list, not a grid. The badge sits in its own column so a reader can
 * scan states down the page; the one action a state allows is always present
 * and never dimmed.
 *
 * "Not installed" is the state of almost every row on a first visit, and the
 * Install button beside it already says so. A column of identical grey words
 * is noise that hides the rows that have something to report, so that one
 * state is left off the row — the drawer still shows it, and the row's
 * accessible name carries it, which is what a reader hears on Tab.
 */
export function LibraryItemRow({
  item,
  onSelect,
  onInstall,
  onUpdate,
  pending = false,
  present,
  items,
}: {
  item: LibraryItemView;
  onSelect: (item: LibraryItemView, from: HTMLElement) => void;
  onInstall: (item: LibraryItemView) => void;
  onUpdate: (item: LibraryItemView) => void;
  pending?: boolean;
  /** `kind/key` of every item this workspace already holds, for the check marks. */
  present?: ReadonlySet<string>;
  /** The whole catalog, so a workflow can say what it has lost. */
  items?: readonly LibraryItemView[];
}) {
  const action = actionFor(item);
  const reasonId = useId();
  const summary =
    item.kind === 'workflow' && (item.requires ?? []).length > 0
      ? rowSummary(item, items ?? [], present ?? new Set())
      : null;
  // The row's accessible name carries every state the badges carry, so a
  // reader hears on Tab what a sighted reader sees — including the one the
  // list does not draw.
  // An installed workflow whose agents or skills have gone is still installed,
  // and nothing downstream will ever say so (spec 107 US1).
  const gone = items ? missingParts(item, items) : [];
  const spoken = [
    ...stateBadges(item.state, item.installable).map((badge) => badge.label),
    ...(gone.length > 0 ? [missingPartsLabel(gone.length)] : []),
  ]
    .map((label) => label.toLowerCase())
    .join(', ');
  // What the keyboard lands on, and where focus returns when the drawer
  // closes: the name, which is the thing being opened.
  // Installing is for the workspace's members (spec 189).
  const canWork = useCanWork();
  const nameRef = useRef<HTMLButtonElement>(null);

  return (
    <article
      aria-label={`${item.name}, ${spoken}`}
      // A convenience, not the control: the name below is the button, and it is
      // what a keyboard reaches. An `article` with a tab index and its own key
      // handling was a control pretending to be a document.
      onClick={() => {
        if (nameRef.current) onSelect(item, nameRef.current);
      }}
      // On a phone the state and the action take the card's top corner, beside
      // the name, and the lines under the name span the card (docs/phone.md
      // PH-06, spec 278): on a line of their own they cost every card 44px.
      className="group hover:bg-accent/60 focus-within:bg-accent/60 grid cursor-pointer grid-cols-1 items-start gap-x-4 gap-y-2 px-4 py-4 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:gap-x-3 max-sm:gap-y-1 transition-colors sm:col-span-full sm:grid-cols-subgrid sm:items-center motion-safe:duration-[var(--motion-duration-base)]"
    >
      {/* No kind mark here. A tab holds one kind, so every row carried the same
          icon and it said nothing — thirteen identical squares down a column.
          The drawer keeps it, and so does the Needs list, where kinds mix. */}
      <div className="flex min-w-0 flex-col gap-1 max-sm:contents max-sm:[&>*:not(h3)]:col-span-2">
        {/* An agent or a skill is named by its folder — the word a prompt
            writes and Files shows — so it is set as the identifier it is.
            A workflow has a title, and keeps the text face. */}
        <h3 className="max-sm:col-start-1 max-sm:row-start-1 max-sm:self-center">
          <button
            ref={nameRef}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSelect(item, event.currentTarget);
            }}
            className={cn(
              'focus-visible:ring-ring touch-pad max-w-full truncate rounded-sm text-sm font-medium focus-visible:ring-3 focus-visible:outline-none',
              item.kind !== 'workflow' && 'font-mono',
            )}
          >
            {item.name}
          </button>
        </h3>
        {/* Two lines, because the curated descriptions are whole sentences and
            one line ended almost every row in an ellipsis; one line on a phone,
            where two would push the action off the first screenful. */}
        <p className="text-muted-foreground line-clamp-1 max-w-[70ch] text-sm sm:line-clamp-2">
          <InlineCode text={item.description} />
        </p>
        {item.origin === 'shared' && item.shared_by && (
          <p className="text-muted-foreground text-xs">
            Shared by {item.shared_by}
          </p>
        )}
        {!item.installable && item.reason && (
          <p id={reasonId} className="text-destructive text-xs">
            {item.reason}
          </p>
        )}
        {/* One line, not chips. Chips took the first three entries of the
            install list, which starts at the innermost workflow, so a nested
            workflow's row led with its leaf's agents (spec 109 US3). The line
            names what the workflow links to, then counts the rest by kind;
            the drawer draws the whole tree. */}
        {summary !== null && (
          <p
            title={summary.text}
            className="text-muted-foreground mt-0.5 truncate text-xs"
          >
            {summary.links.length > 0 && (
              <>
                Links{' '}
                {summary.links.map((link, index) => (
                  <span key={link.key}>
                    {index > 0 && ', '}
                    {/*
                      Inline in the sentence, not an `inline-flex` box: a flex
                      box takes its baseline from its first item, and an icon
                      has none, so the name inside rode 3px above "Links"
                      beside it. The icon drops onto the cap band the way
                      `StepKindIcon` sits in its line.
                    */}
                    <span className="text-foreground">
                      {link.present && (
                        <>
                          <CheckIcon
                            aria-hidden="true"
                            className="mr-0.5 inline-block size-3 align-[-0.15em]"
                          />
                          {/* Read aloud with its space, as the chips were. */}
                          <span className="sr-only">{'already here: '}</span>
                        </>
                      )}
                      {link.name}
                    </span>
                  </span>
                ))}
              </>
            )}
            {summary.needs &&
              `${summary.links.length > 0 ? ' · needs ' : 'Needs '}${summary.needs}`}
          </p>
        )}
      </div>

      {/* State and action end at one right edge, in one column. They were two,
          which left a badge short of where the buttons ended and the eye
          tracking two rules down the page instead of one. A row can carry both
          — an outdated agent says "Installed · Update available" beside its
          Update button — so they flow, badges first and the action last. */}
      <div className="flex flex-wrap items-center justify-end gap-2 sm:col-start-2 max-sm:col-start-2 max-sm:row-start-1">
        <StateBadge
          state={item.state}
          installable={item.installable}
          reasonId={!item.installable && item.reason ? reasonId : undefined}
          // The state almost every row is in on a first visit, said by the
          // Install button beside it; thirty identical grey badges would hide
          // the rows that have something to report.
          omit={['not_installed']}
          also={
            gone.length > 0
              ? [
                  {
                    fact: 'incomplete',
                    label: missingPartsLabel(gone.length),
                    tone: 'warning',
                  },
                ]
              : undefined
          }
        />
        {action && canWork && (
          <Button
            size="sm"
            variant={action === 'install' ? 'default' : 'outline'}
            disabled={pending}
            onClick={(event) => {
              event.stopPropagation();
              if (action === 'install') onInstall(item);
              else onUpdate(item);
            }}
            // Never dimmed (spec 129 FR-012): at 70% beside the "Update
            // available" badge, Update read as no action at all.
          >
            {action === 'install' ? (
              <DownloadIcon aria-hidden="true" />
            ) : (
              <RefreshCwIcon aria-hidden="true" />
            )}
            {action === 'install' ? 'Install' : 'Update'}
          </Button>
        )}
        {/* A workflow is not updated in place — it is deleted and installed
            again, which the drawer explains. The badge still gets a way on
            beside it rather than standing alone (spec 129 FR-012). */}
        {!action &&
        item.installable &&
        item.kind === 'workflow' &&
        item.state === 'update_available' ? (
          <Button
            size="sm"
            variant="outline"
            onClick={(event) => {
              event.stopPropagation();
              if (nameRef.current) onSelect(item, nameRef.current);
            }}
          >
            <RefreshCwIcon aria-hidden="true" />
            How to update
          </Button>
        ) : null}
      </div>
    </article>
  );
}
